import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {Router} from '@angular/router';
import {Observable, ReplaySubject, Subject, throwError} from 'rxjs';
import {CachedItem, NgForageCache} from 'ngforage';
import {environment} from '../../../environments/environment';
import {CookieService} from 'ngx-cookie-service';
import {NgxSpinnerService} from 'ngx-spinner';

@Injectable()
export class UtilsService {
  public messagesForToasts$ = new Subject<any>();
  public simpleMessageData: { header: string; isVisible: boolean; message: string; buttonLabel: string } = {
    isVisible: false,
    header: null,
    message: 'Content',
    buttonLabel: 'Ok',
  };

  constructor(
    public router: Router,
    private spinner: NgxSpinnerService,
    private cookieService: CookieService,
    public readonly ngForageCache: NgForageCache,
  ) {
  }

  public handleErrorWebservice$(response: any, message = '', type = 'error'): any {
    let fullTitle: string;
    let fullMessage = '';
    if (environment.production === true) {
      this.unlockUi();
      return throwError(response);
    } else {
      fullTitle = 'Error on webservice call';
      fullMessage = '';
      let messageSep = '';
      if (message && message.length) {
        fullMessage += messageSep + message;
        messageSep = '<br>';
      }
      if (response.message && response.message.length) {
        fullMessage += messageSep + response.message;
        messageSep = '<br>';
      }
      if (response.error && response.error['title']) {
        fullTitle += ': ' + response.error['title'];
        fullMessage += messageSep + '<b>' + response.error['description'] + '</b>';
        messageSep = '<br>';
      } else if (response.error && response.error.currentTarget && response.error.currentTarget.__zone_symbol__xhrURL) {
        fullTitle += ': ' + 'No response from API.';
        fullMessage += messageSep + 'Url: ' + response.error.currentTarget.__zone_symbol__xhrURL;
        messageSep = '<br>';
      }
      if (response.graphQLErrors && response.graphQLErrors.length) {
        _.forEach(response.graphQLErrors, (gqlError: any) => {
          fullMessage += messageSep + gqlError.debugMessage;
          messageSep = '<br>';
        });
      }
    }
    this.displayMessage(fullTitle, fullMessage, type);
    this.unlockUi();
    return throwError(response);
  }

  public navigate(newUrl: string) {
    setTimeout(() => {
      this.router.navigateByUrl(newUrl);
    }, 100);
  }

  public displayMessage(
    title: string,
    message: any = '',
    type = 'success',
    keepAfterRouteChange = false,
    allowHtml = false,
    data: any = null
  ) {
    if (type === 'warning') {
      type = 'warn';
    }
    let sticky = false;
    if (type === 'error') {
      sticky = true;
    }

    const toast: any = {
      severity: type,
      summary: title,
      detail: message,
      sticky,
      closable: allowHtml ? false : true,
      life: 6000,
      keepAfterRouteChange,
      allowHtml,
      data
    };

    this.messagesForToasts$.next(toast);
  }

  public saveUserData(key: string, value: any) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      localStorage.removeItem(key);
      this.cleanLocalStorage([]);
    }
  }

  public saveCookie(key: string, value: any, expires?: number | Date): void {
    const suffix = environment.subCookiesDomain;
    if (!key.endsWith(suffix)) {
      key += suffix;
    }
    // store suffix in the cookie's key to distinguish the stored variables between different environments
    this.cookieService.set(key, value, expires !== null ? expires : null, '/', environment.cookieDomain, true, 'None');
    if (!this.getCookie(key)) {
      this.cookieService.set(key, value, expires !== null ? expires : null, '/', environment.cookieDomain, null, null);
    }
  }

  public getCookie(key: string): string {
    const suffix = environment.subCookiesDomain;
    if (!key.endsWith(suffix)) {
      key += suffix;
    }
    return this.cookieService.get(key);
  }

  public deleteCookie(cookieName: string) {
    const suffix = environment.subCookiesDomain;
    if (!cookieName.endsWith(suffix)) {
      cookieName += suffix;
    }
    this.cookieService.delete(cookieName);
    this.cookieService.delete(cookieName, '/');
    this.cookieService.delete(cookieName, '/', environment.cookieDomain);
  }

  public getUserData(key: string) {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch (e) {
      return null;
    }
  }

  public loadUserPref(key: string, defaultValue = null, dataTypeToConvertTo = 'json'): any {
    let storageValue;
    storageValue = localStorage.getItem(key);
    if (storageValue === null || storageValue === 'undefined') {
      return defaultValue;
    }
    let userPrefValue = JSON.parse(storageValue);
    if (dataTypeToConvertTo === 'date') {
      userPrefValue = new Date(userPrefValue);
    }
    return userPrefValue;
  }

  public removeUserData(key: string) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
    }
  }

  public cleanLocalStorage(patternsToRemove = [], isInLocalStorage = true): void {
    const keysInStorage = [];
    patternsToRemove = [].concat(patternsToRemove); // prevent undefined

    let storage = localStorage;
    if (!isInLocalStorage) {
      storage = sessionStorage;
    }

    _.forEach(Object.keys(storage), (key) => {
      keysInStorage.push(key);
    });

    // loop through keys
    for (const key of keysInStorage) {
      let deleteItem = false;

      // check if key excluded
      for (const patternRemove of patternsToRemove) {
        if (key.length > patternRemove.length && key.substring(0, patternRemove.length) === patternRemove) {
          deleteItem = true;
        }
      }
      if (deleteItem && key) {
        storage.removeItem(key);
      }
    }
  }

  public lockUi() {
    this.spinner.show();
  }

  public unlockUi() {
    this.spinner.hide();
  }

  public redirectToNotFound() {
    this.router.navigate(['/404'], {
      replaceUrl: true,
      state: {notFoundUrl: this.router.url, canGoBack: history.state?.navigationId > 1}
    });
  }

  public editorHtmlWithBreakableSpaces(html: string): string {
    return html?.replace(/(?:&nbsp;)+/g, run => ' ' + '&nbsp;'.repeat(run.length / 6 - 1));
  }

  // use Forage for long term storage
  public async cacheablePromise<T>(promiseFunction: () => Promise<T>, key: string, forceReload: boolean = false, loadIfInCache = true): Promise<any> {
    const fullKey = 'dataPromise_' + key;

    const cachedData = await this.getItemFromForage(fullKey);
    if (cachedData && !forceReload) {

      // always call the function and cache it for next time... add a delay...
      let delay = 10000 + Math.random() * 10000;
      if (forceReload) {
        delay = 0;
      }
      if (loadIfInCache) {
        setTimeout(() => {
          promiseFunction()
            .then((fullResponse) => {
              return this.setItemToForage(fullKey, fullResponse);
            });
        }, delay);
      }
      return Promise.resolve(cachedData);
    }
    return promiseFunction()
      .then((fullResponse) => {
        return this.setItemToForage(fullKey, fullResponse);
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  public cacheableObservable<T>(returnObservable: () => Observable<T>, key?: string, forceReload: boolean = false, showMessageWhenDataChanged: string = null, addCachedData = false): Observable<T> {
    const replay = new ReplaySubject<T>();
    const fullKey = 'data_' + key;
    let isFirstLoad = true;
    let cachedDataGlob = null;
    let cachedDataAsStringForComparing = null;
    this.getItemFromForage(fullKey).then((cachedData) => {
      cachedDataAsStringForComparing = JSON.stringify(cachedData);
      cachedDataGlob = cachedData;
      if (cachedData && !forceReload) {
        isFirstLoad = false;
        if (addCachedData) {
          cachedData.isCached = true;
          const cachedDataWithCacheInfo = _.clone(cachedData);
          cachedDataWithCacheInfo.isCached = true;
          replay.next(cachedDataWithCacheInfo);
        } else {
          replay.next(cachedData);
        }
      }
    });

    returnObservable().subscribe({
        next: fullResponse => {
          this.setItemToForage(fullKey, fullResponse).then(() => {
            if (isFirstLoad) {
              replay.next(fullResponse);
              replay.complete();
            } else {
              this.getItemFromForage(fullKey).then((newCachedData) => {
                const newCachedDataAsStringForComparing = JSON.stringify(newCachedData);
                if (_.isEqual(newCachedDataAsStringForComparing, cachedDataAsStringForComparing)) {
                } else {
                  replay.next(fullResponse);
                  replay.complete();
                }
              });
            }
          });
        },
        error: (error) => {
          if (!cachedDataGlob) {
            replay.error(error);
          } else {
            this.handleErrorWebservice$(error, null, 'warn');
            replay.complete();
          }
        },
      }
    );
    return replay.asObservable();
  }

  public async setItemToForage<T = any>(key: string, value: any, ttl = 31 * 24 * 60 * 60 * 1000): Promise<T> {
    const promise = this.ngForageCache.setCached<T>(key, value, ttl);
    promise.catch(() => {
    });
    return promise;
  }

  public getItemFromForage<T = any>(key: string): Promise<T | null> {
    return this.ngForageCache.getCached<T>(key)
      .then((r: CachedItem<T>) => {
        if (!r.hasData || r.expired) {
          return null;
        }
        return r.data;
      });
  }

  getBrandName() {
    if (window.location.origin === environment.mckayDomaine) {
      return environment.mckayBrand;
    } else if (window.location.origin === environment.quincyDomaine) {
      return environment.quincyBrand;
    } else {
      return '';
    }
  }

  htmlDecode(name) {
    const txt = document.createElement('textarea');
    txt.innerHTML = name;
    return txt.value;
  }

  b64toBlob(b64Data, contentType, sliceSize) {
    contentType = contentType || 'application/pdf';
    sliceSize = sliceSize || 512;
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, {type: contentType});
  }

  getClassForAnnouncementCategory(announcementCategory: any) {
    if (announcementCategory === 'outage') {
      return 'outage-category';
    }
    if (announcementCategory === 'emergency_maintenance') {
      return 'emergency-maintenance-category';
    }
    if (announcementCategory === 'scheduled_maintenance') {
      return 'scheduled-maintenance-category';
    }
    if (announcementCategory === 'service_change') {
      return 'service-change-category';
    }
    if (announcementCategory === 'marketing') {
      return 'marketing-category';
    }
    if (announcementCategory === 'client_action_required') {
      return 'client-action-required-category';
    }
    return '';
  }
}
